import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Popover, PopoverContent, PopoverAnchor } from '@/components/ui/popover';
import {
  Loader2,
  Users,
  User,
  UserPlus,
  CheckCircle2,
  Search,
  Mail,
  Phone,
  Check,
} from 'lucide-react';
import { PhoneInput } from '@/components/ui/phone-input';
import { toast } from 'sonner';
import axios from '@/lib/axios';
import { type FormField, type FormAnswerValue } from '@/types';
import RegistrationFormDialog, { type RegistrationFormHandle } from './RegistrationFormDialog';
import { useDebounce } from '@/hooks/use-debounce-custom';
import { type Member } from '@/types';
import { useUser } from '@/hooks/use-user';

interface Ticket {
  id: string;
  name: string;
  price: number;
  description?: string;
  formSchema?: FormField[] | null;
  type?: 'MEMBER_ONLY' | 'MEMBER_GUEST' | 'OPEN_FOR_ALL';
  allowedAttendeeTypes?: string[];
}

interface CartItem {
  ticket: Ticket;
  count: number;
}

interface Attendee {
  type: 'self' | 'member' | 'guest';
  userId?: string;
  name?: string;
  email?: string;
  phone?: string;
}

interface RegistrationData {
  ticketId: string;
  attendee: Attendee;
  formData: Record<string, FormAnswerValue>;
}

interface CheckoutDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  communityId: string;
  cart: CartItem[];
  eventId: string;
  isMember: boolean;
  isRegistrationClosed: boolean;
  allowDuplicatePhone: boolean;
  onSuccess: () => void;
  checkoutBgImage?: string | null;
  lockSelfAttendee?: boolean;
  selfRegistered?: boolean;
}

const CheckoutDialog: React.FC<CheckoutDialogProps> = ({
  open,
  onOpenChange,
  communityId,
  cart,
  eventId,
  isMember,
  allowDuplicatePhone,
  onSuccess,
  checkoutBgImage,
  lockSelfAttendee = false,
  selfRegistered = false,
}) => {
  const { user } = useUser();
  const [step, setStep] = useState(1);
  const [registrations, setRegistrations] = useState<RegistrationData[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRegistering, setIsRegistering] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Member[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const formRef = useRef<RegistrationFormHandle>(null);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  useEffect(() => {
    if (!scrollEl) return;
    const update = () => {
      setShowTopShadow(scrollEl.scrollTop > 1);
      setShowBottomShadow(scrollEl.scrollTop + scrollEl.clientHeight < scrollEl.scrollHeight - 1);
    };
    update();
    scrollEl.addEventListener('scroll', update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(scrollEl);
    const mutationObserver = new MutationObserver(update);
    mutationObserver.observe(scrollEl, { childList: true, subtree: true });
    return () => {
      scrollEl.removeEventListener('scroll', update);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [scrollEl]);

  // Helper: build the flat registration list from the cart
  const buildRegistrations = useCallback(() => {
    const flat: RegistrationData[] = [];

    cart.forEach((item) => {
      const allowedTypes = item.ticket.allowedAttendeeTypes || ['GUEST'];

      for (let i = 0; i < item.count; i++) {
        let defaultType: Attendee['type'] = 'guest';

        if (lockSelfAttendee && isMember && allowedTypes.includes('SELF') && !selfRegistered) {
          defaultType = 'self';
        } else if (
          isMember &&
          !selfRegistered &&
          allowedTypes.includes('SELF') &&
          !flat.some((r) => r.attendee.type === 'self')
        ) {
          defaultType = 'self';
        } else if (isMember && allowedTypes.includes('MEMBER')) {
          defaultType = 'member';
        } else {
          defaultType = 'guest';
        }

        flat.push({
          ticketId: item.ticket.id,
          attendee: {
            type: defaultType,
            ...(defaultType === 'self' && user
              ? {
                  name: [user.firstName, user.middleName, user.lastName].filter(Boolean).join(' '),
                  email: user.email || undefined,
                  phone: user.phone
                    ? user.phone.startsWith('+')
                      ? user.phone
                      : `+${user.phone}`
                    : undefined,
                  userId: user.id,
                }
              : {}),
          },
          formData: {},
        });
      }
    });
    return flat;
  }, [cart, user, isMember, lockSelfAttendee, selfRegistered]);

  // Debounced search for members
  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(async () => {
      if (searchQuery.trim().length < 2) {
        setSearchResults([]);
        return;
      }
      setIsSearching(true);
      try {
        const res = await axios.get(
          `/api/communities/${communityId}/members?search=${searchQuery}`
        );
        setSearchResults(res.data.members || []);
      } catch (err) {
        console.error('Member search failed:', err);
      } finally {
        setIsSearching(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery, communityId, open]);

  // Populate registrations when the dialog opens, reset when it closes.
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current && cart.length > 0) {
      setRegistrations(buildRegistrations());
    }
    if (!open && prevOpen.current) {
      setStep(1);
      setRegistrations([]);
      setCurrentIndex(0);
      setSearchQuery('');
      setSearchResults([]);
    }
    prevOpen.current = open;
  }, [open, buildRegistrations, cart.length]);

  const [isChildFormValid, setIsChildFormValid] = useState(true);

  // Reset child validation when index changes
  useEffect(() => {
    setIsChildFormValid(true);
  }, [currentIndex]);

  const currentReg = registrations[currentIndex];
  const currentTicket = cart.find((item) => item.ticket.id === currentReg?.ticketId)?.ticket;

  const debouncedPhone = useDebounce(currentReg?.attendee.phone || '', 500);

  useEffect(() => {
    const checkPhoneDuplicate = async () => {
      if (!debouncedPhone || debouncedPhone.length < 10) {
        setPhoneError(null);
        return;
      }

      if (allowDuplicatePhone !== false) {
        setPhoneError(null);
        return;
      }

      try {
        const res = await axios.get(
          `/api/events/${eventId}/check-phone?phone=${encodeURIComponent(debouncedPhone)}`
        );
        if (res.data.exists) {
          setPhoneError(`This phone number is already registered for this event.`);
        } else {
          setPhoneError(null);
        }
      } catch (err) {
        console.error('Error checking phone:', err);
      }
    };

    checkPhoneDuplicate();
  }, [debouncedPhone, eventId, allowDuplicatePhone]);

  const handleAttendeeChange = (attendee: Attendee) => {
    setFieldErrors({});
    const updated = [...registrations];
    updated[currentIndex].attendee = attendee;
    setRegistrations(updated);
  };

  const handleFormSubmit = async (formData: Record<string, FormAnswerValue>) => {
    // Validate guest information if type is guest
    if (currentReg?.attendee.type === 'guest') {
      const errors: Record<string, string> = {};

      if (!currentReg.attendee.name?.trim()) {
        errors.name = "Please provide the guest's name";
      }
      if (!currentReg.attendee.email?.trim() && !currentReg.attendee.phone?.trim()) {
        errors.email = 'Please provide either an email or phone number';
      } else if (
        currentReg.attendee.email?.trim() &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentReg.attendee.email.trim())
      ) {
        errors.email = 'Please enter a valid email address';
      }

      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        toast.error(Object.values(errors)[0]);
        return;
      }
    }
    // Validate member information
    if (currentReg?.attendee.type === 'member' && !currentReg.attendee.userId) {
      toast.error('Please search and select a member');
      return;
    }

    const updated = [...registrations];
    updated[currentIndex].formData = formData;
    setRegistrations(updated);

    if (currentIndex < registrations.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setStep(2); // Review Step
    }
  };

  const handleBulkRegister = async () => {
    setIsRegistering(true);
    try {
      await axios.post(`/api/events/${eventId}/bulk-register`, { registrations });
      toast.success('Registration successful!');
      onSuccess();
      onOpenChange(false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error(err.response?.data?.error || 'Registration failed');
    } finally {
      setIsRegistering(false);
    }
  };

  // Always render the Dialog so it stays mounted in the React tree.
  // Previously `return null` here caused the Dialog to unmount/remount
  // on every open, leading Radix to misinterpret the triggering click
  // as an outside-click and immediately close the dialog.
  const hasRegistrations = registrations.length > 0;

  return (
    <Dialog open={open && hasRegistrations} onOpenChange={onOpenChange}>
      <DialogContent closeOutside syncBrowserHistory className="max-w-2xl">
        {checkoutBgImage && (
          <div
            className="pointer-events-none absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-[0.1]"
            style={{ backgroundImage: `url(${checkoutBgImage})` }}
          />
        )}
        <DialogHeader className="relative z-10 shrink-0 p-4 pb-0 pr-12 sm:p-6 sm:pb-0">
            <DialogTitle className="flex flex-col gap-2 text-xl sm:flex-row sm:items-center sm:justify-between">
              <span>
                {step === 1
                  ? `Attendee ${currentIndex + 1} of ${registrations.length}`
                  : 'Review Registration'}
              </span>
              {step === 1 && (
                <span className="w-fit max-w-full truncate text-xs font-medium text-primary bg-primary/10 px-3 py-1 rounded-full border border-primary/20">
                  {currentTicket?.name}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="mt-1.5">
              {step === 1
                ? lockSelfAttendee
                  ? 'Tell us about yourself to complete your registration.'
                  : `Provide details for this ticket.`
                : 'Review your order before proceeding to payment.'}
            </DialogDescription>
          </DialogHeader>

          <div className="relative z-10 flex min-h-0 flex-1 flex-col">
            {showTopShadow && (
              <div className="pointer-events-none absolute inset-x-0 top-0 z-20 h-8 bg-gradient-to-b from-foreground/20 to-transparent" />
            )}
            {showBottomShadow && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-8 bg-gradient-to-t from-foreground/20 to-transparent" />
            )}
            <div ref={setScrollEl} className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
              {step === 1 && currentReg && (
                <div className="space-y-6">
                  {!lockSelfAttendee && (
                    <div className="space-y-3">
                      <Label>Who is attending?</Label>
                      <RadioGroup
                        value={currentReg.attendee.type}
                        onValueChange={(val: Attendee['type']) => {
                          if (val === 'self') {
                            const alreadyHasSelf = registrations.some(
                              (r, i) => i !== currentIndex && r.attendee.type === 'self'
                            );
                            if (alreadyHasSelf) {
                              toast.error('You can only select "Self" for one ticket.');
                              return;
                            }
                            // Auto-fill self details
                            if (user) {
                              handleAttendeeChange({
                                type: 'self',
                                name: [user.firstName, user.middleName, user.lastName]
                                  .filter(Boolean)
                                  .join(' '),
                                email: user.email || undefined,
                                phone: user.phone
                                  ? user.phone.startsWith('+')
                                    ? user.phone
                                    : `+${user.phone}`
                                  : undefined,
                                userId: user.id,
                              });
                              return;
                            }
                          }
                          setSearchQuery('');
                          setSearchResults([]);
                          handleAttendeeChange({ type: val });
                        }}
                        className="grid grid-cols-3 gap-2 sm:gap-3"
                      >
                        {[
                          {
                            id: 'self',
                            label: 'Self',
                            icon: User,
                            show:
                              !selfRegistered &&
                              isMember &&
                              (currentTicket?.allowedAttendeeTypes?.includes('SELF') ?? true),
                          },
                          {
                            id: 'member',
                            label: 'Member',
                            icon: Users,
                            show:
                              isMember &&
                              (currentTicket?.allowedAttendeeTypes?.includes('MEMBER') ?? true),
                          },
                          {
                            id: 'guest',
                            label: 'Guest',
                            icon: UserPlus,
                            show: currentTicket?.allowedAttendeeTypes?.includes('GUEST') ?? true,
                          },
                        ]
                          .filter((opt) => opt.show)
                          .map((opt) => (
                            <div key={opt.id} className="relative">
                              <RadioGroupItem
                                value={opt.id}
                                id={`${opt.id}-${currentIndex}`}
                                className="peer sr-only"
                              />
                              <Label
                                htmlFor={`${opt.id}-${currentIndex}`}
                                className="flex flex-col items-center justify-center rounded-xl border-2 border-muted bg-popover p-2 sm:p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 transition-all cursor-pointer group"
                              >
                                <opt.icon className="mb-1 sm:mb-2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                                <span className="text-xs sm:text-sm font-semibold">
                                  {opt.label}
                                </span>
                              </Label>
                              {currentReg?.attendee.type === opt.id && (
                                <div className="absolute -top-1 -right-1">
                                  <div className="bg-primary rounded-full p-0.5">
                                    <Check className="h-3 w-3 text-primary-foreground" />
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                      </RadioGroup>
                    </div>
                  )}

                  {lockSelfAttendee && currentReg.attendee.type === 'self' && user && (
                    <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-xl border-2 border-primary/10">
                      <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center text-primary text-base font-bold shadow-inner">
                        {(currentReg.attendee.name || user.firstName || '?').charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold truncate">
                          {currentReg.attendee.name ||
                            [user.firstName, user.middleName, user.lastName]
                              .filter(Boolean)
                              .join(' ')}
                        </p>
                        <p className="text-xs text-muted-foreground">Registering as yourself</p>
                      </div>
                    </div>
                  )}

                  {!lockSelfAttendee && currentReg?.attendee.type === 'guest' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 border p-4 rounded-lg bg-muted/30">
                      <div className="flex flex-col justify-end space-y-2">
                        <Label>Name</Label>
                        <Input
                          placeholder="Guest Name"
                          value={currentReg?.attendee.name || ''}
                          onChange={(e) =>
                            handleAttendeeChange({ ...currentReg?.attendee, name: e.target.value })
                          }
                          className={fieldErrors.name ? 'border-destructive' : undefined}
                        />
                        {fieldErrors.name && (
                          <p className="text-xs text-destructive">{fieldErrors.name}</p>
                        )}
                      </div>
                      <div className="flex flex-col justify-end space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Email</Label>
                          {user?.email && (
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-xs text-primary"
                              onClick={() =>
                                handleAttendeeChange({
                                  ...currentReg?.attendee,
                                  email: user.email || undefined,
                                })
                              }
                            >
                              Use my Email
                            </Button>
                          )}
                        </div>
                        <Input
                          placeholder="guest@example.com"
                          value={currentReg?.attendee.email || ''}
                          onChange={(e) =>
                            handleAttendeeChange({ ...currentReg?.attendee, email: e.target.value })
                          }
                          className={fieldErrors.email ? 'border-destructive' : undefined}
                        />
                        {fieldErrors.email && (
                          <p className="text-xs text-destructive">{fieldErrors.email}</p>
                        )}
                      </div>
                      <div className="flex flex-col justify-end space-y-2 sm:col-span-2">
                        <div className="flex items-center justify-between">
                          <Label>Phone</Label>
                          {user?.phone && (
                            <Button
                              variant="link"
                              size="sm"
                              className="h-auto p-0 text-xs text-primary"
                              onClick={() =>
                                handleAttendeeChange({
                                  ...currentReg?.attendee,
                                  phone: user.phone
                                    ? user.phone.startsWith('+')
                                      ? user.phone
                                      : `+${user.phone}`
                                    : undefined,
                                })
                              }
                            >
                              Use my Phone
                            </Button>
                          )}
                        </div>
                        <PhoneInput
                          placeholder="Phone number"
                          value={currentReg?.attendee.phone || ''}
                          onChange={(val) =>
                            handleAttendeeChange({ ...currentReg?.attendee, phone: val })
                          }
                          className={phoneError ? 'border-destructive' : undefined}
                        />
                        {phoneError && (
                          <p className="text-xs text-destructive font-medium">{phoneError}</p>
                        )}
                      </div>
                    </div>
                  )}

                  {!lockSelfAttendee && currentReg.attendee.type === 'member' && (
                    <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
                      <div className="space-y-2">
                        <Label>Search Member</Label>
                        <Popover
                          open={
                            searchQuery.trim().length >= 2 &&
                            (isSearching ||
                              searchResults.length > 0 ||
                              (searchQuery.trim().length >= 2 &&
                                !isSearching &&
                                searchResults.length === 0))
                          }
                        >
                          <PopoverAnchor asChild>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input
                                placeholder="Search by name or phone..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9"
                              />
                              {isSearching && (
                                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                              )}
                            </div>
                          </PopoverAnchor>
                          <PopoverContent
                            className="p-0 border rounded-xl shadow-2xl overflow-hidden max-h-[350px] overflow-y-auto w-[var(--radix-popover-anchor-width)] animate-in fade-in zoom-in-95 duration-200"
                            align="start"
                            sideOffset={8}
                            onOpenAutoFocus={(e) => e.preventDefault()}
                          >
                            {searchResults.length > 0 ? (
                              <div className="flex flex-col">
                                {searchResults.map((member) => {
                                  const isEmailSearch = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                                    searchQuery
                                  );
                                  return (
                                    <div
                                      key={member.id}
                                      className={`p-3 flex items-center gap-3 hover:bg-accent/50 cursor-pointer border-b last:border-0 transition-colors ${currentReg?.attendee.userId === member.id ? 'bg-primary/5' : ''}`}
                                      onClick={() => {
                                        // Check if this member is already selected in another ticket
                                        const isAlreadySelected = registrations.some(
                                          (r, i) =>
                                            i !== currentIndex && r.attendee.userId === member.id
                                        );

                                        if (isAlreadySelected) {
                                          toast.error(
                                            `${member.name} is already selected for another ticket in this order.`
                                          );
                                          return;
                                        }

                                        handleAttendeeChange({
                                          ...currentReg?.attendee,
                                          userId: member.id,
                                          name: member.name,
                                          email: member.email || undefined,
                                          phone: member.phone || undefined,
                                        });
                                        setSearchQuery('');
                                        setSearchResults([]);
                                      }}
                                    >
                                      <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold shrink-0">
                                        {member.name.charAt(0)}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold truncate">
                                          {member.name}
                                        </p>
                                        <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                          {isEmailSearch ? (
                                            <>
                                              <Mail className="h-3 w-3 shrink-0" /> {member.email}
                                            </>
                                          ) : (
                                            <>
                                              <Phone className="h-3 w-3 shrink-0" /> {member.phone}
                                            </>
                                          )}
                                        </p>
                                      </div>
                                      {currentReg?.attendee.userId === member.id && (
                                        <div className="bg-primary/20 p-1 rounded-full">
                                          <Check className="h-3 w-3 text-primary" />
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : searchQuery.trim().length >= 2 &&
                              !isSearching &&
                              searchResults.length === 0 ? (
                              <div className="p-4 text-center text-sm text-muted-foreground bg-background">
                                No members found.
                              </div>
                            ) : null}
                          </PopoverContent>
                        </Popover>
                      </div>

                      {currentReg?.attendee.userId && (
                        <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-xl border-2 border-primary/10 transition-all hover:border-primary/20">
                          <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center text-primary text-base font-bold shadow-inner">
                            {currentReg?.attendee.name?.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold truncate">
                              {currentReg?.attendee.name}
                            </p>
                            <div className="flex flex-col gap-1 mt-0.5">
                              <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                <Phone className="h-3 w-3" /> {currentReg?.attendee.phone}
                              </p>
                              <p className="text-xs text-muted-foreground flex items-center gap-1 truncate">
                                <Mail className="h-3 w-3" /> {currentReg?.attendee.email}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-primary/20 text-primary hover:bg-primary/10 font-semibold h-8 rounded-lg"
                            onClick={() => handleAttendeeChange({ type: 'member' })}
                          >
                            Change
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {currentTicket?.formSchema && currentTicket.formSchema.length > 0 ? (
                    <div className="pt-6 border-t mt-6">
                      <h4 className="text-sm font-bold mb-4 flex items-center text-primary/80">
                        Additional Details
                      </h4>
                      <RegistrationFormDialog
                        key={currentIndex}
                        open={true}
                        onOpenChange={() => {}}
                        ticketName={currentTicket?.name || ''}
                        ticketPrice={currentTicket?.price || 0}
                        formSchema={currentTicket?.formSchema || []}
                        onValidationChange={setIsChildFormValid}
                        onSubmit={handleFormSubmit}
                        isInline={true}
                        submitButtonText="Next"
                        hideSubmitButton={true}
                        ref={formRef}
                        eventId={eventId}
                        allowDuplicatePhone={allowDuplicatePhone}
                      />
                    </div>
                  ) : null}
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4 py-4">
                  {registrations.map((reg, idx) => {
                    const ticket = cart.find((c) => c.ticket.id === reg.ticketId)?.ticket;
                    return (
                      <div
                        key={idx}
                        className="p-4 border rounded-lg flex items-start justify-between"
                      >
                        <div className="space-y-1">
                          <p className="font-semibold">{ticket?.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Attendee:{' '}
                            {reg.attendee.type === 'self'
                              ? 'You (Self)'
                              : reg.attendee.name || reg.attendee.userId || 'Guest'}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">₹{ticket?.price || 0}</p>
                          <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-primary text-primary-foreground text-[10px] uppercase">
                            {reg.attendee.type}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="p-4 bg-primary/5 rounded-lg flex justify-between items-center font-bold text-lg">
                    <span>Total Amount</span>
                    <span>
                      ₹
                      {registrations.reduce(
                        (acc, reg) =>
                          acc + (cart.find((c) => c.ticket.id === reg.ticketId)?.ticket.price || 0),
                        0
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="relative z-10 shrink-0 gap-2 border-t border-muted-foreground/60 bg-background p-4 sm:flex-row sm:justify-end sm:p-6">
            {step === 2 && (
              <>
                <Button
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() => setStep(1)}
                  disabled={isRegistering}
                >
                  Back to Details
                </Button>
                <Button
                  className="w-full sm:w-auto"
                  onClick={handleBulkRegister}
                  disabled={isRegistering}
                >
                  {isRegistering ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="mr-2 h-4 w-4" />
                  )}
                  Confirm & Register
                </Button>
              </>
            )}
            {step === 1 && (
              <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
                {currentIndex > 0 ? (
                  <Button
                    variant="outline"
                    className="w-full sm:w-auto"
                    onClick={() => setCurrentIndex(currentIndex - 1)}
                  >
                    Previous
                  </Button>
                ) : (
                  <span className="hidden sm:block" />
                )}
                <Button
                  className="w-full sm:w-auto"
                  onClick={() => {
                    if (currentTicket?.formSchema && currentTicket.formSchema.length > 0) {
                      formRef.current?.submit();
                    } else {
                      handleFormSubmit({});
                    }
                  }}
                  disabled={!!phoneError || !isChildFormValid}
                >
                  Next
                </Button>
              </div>
            )}
          </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutDialog;
